import React, { FC } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { assistantConversationTemplate } from 'apollo/templates/assistant';
import { AssistantChatMessage } from 'models/assistant';
import InfoText from 'components/shared/info-text.component';
import AssistantMessageBubble from './assistant-message-bubble.component';
import { LARGER_CHAT_TEXT } from './assistant-chat.component';

type PastConversation = { _id: string; title: string; createdAt: string; messages: AssistantChatMessage[] };

type AssistantPastConversationProps = {
    conversationId: string;
    onArticleClicked: (articleId: string) => void;
    onNavigate: (path: string) => void;
    onShowUiElement: (name: string) => void;
};

// A past conversation offers no problem report: drafts are never stored.
const noProblemReport = async () => false;

/** DD-3780: a stored conversation, read-only. Links in it still work. */
const AssistantPastConversation: FC<AssistantPastConversationProps> = ({
    conversationId,
    onArticleClicked,
    onNavigate,
    onShowUiElement,
}: AssistantPastConversationProps) => {
    const { t: translate } = useTranslation(['help-drawer']);
    const { data, loading, error } = useQuery<{ assistantConversation: PastConversation | null }>(
        assistantConversationTemplate,
        { variables: { id: conversationId }, fetchPolicy: 'network-only' },
    );
    if (loading) {
        return <CircularProgress size={24} />;
    }
    const conversation = data?.assistantConversation;
    if (error || !conversation) {
        return <Alert severity="info">{translate('help-drawer:ASSISTANT_PAST_CONVERSATION_GONE')}</Alert>;
    }
    return (
        <Box sx={LARGER_CHAT_TEXT}>
            <Typography variant="caption" color="grey">
                {format(new Date(conversation.createdAt), 'yyyy-MM-dd HH:mm')}
            </Typography>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50', px: 2, my: 1 }}>
                <Stack spacing={1.5} py={1}>
                    {conversation.messages.map((message, index) => (
                        <AssistantMessageBubble
                            key={index}
                            message={message}
                            onArticleClicked={onArticleClicked}
                            onNavigate={onNavigate}
                            onShowUiElement={onShowUiElement}
                            onSendProblemReport={noProblemReport}
                        />
                    ))}
                </Stack>
            </Box>
            <InfoText text={translate('help-drawer:ASSISTANT_PAST_CONVERSATION_READ_ONLY')} />
        </Box>
    );
};

export default AssistantPastConversation;
